import _ from 'lodash'
import moment from 'moment/moment'
import Mustache from 'mustache'
import { delay } from 'redux-saga'
import { select, put, call, fork } from 'redux-saga/effects'

import biMessages from 'core/data-model/constants/bi-messages'
import DEFAULT from 'core/data-model/constants/defaults'
import MESSAGES from 'core/data-model/constants/validation-messages'
import values from 'core/data-model/constants/values'
import { transformOccupationFactor } from 'core/service/insured/occupation'
import { _validateBirthdate } from 'core/service/insured/validation'
import { createChecker, valid } from 'core/service/lib/check'
import { formatNumber } from 'core/service/lib/number-format'
import { generateRiderBenefitData } from 'core/service/rider/benefits'
import { isAgeAllowed } from 'core/service/rider/rider'
import { policy_basic } from 'core/service-wrapper/partner-api/handler-product/basic'
import { riderHandler } from 'core/service-wrapper/partner-api/handler-product/riders'
import { partnerApiHandlerHelper } from 'core/service-wrapper/partner-api/handler-request/service-handler-helper'
import { partnerApiRequest } from 'core/service-wrapper/partner-api/handler-request/service-request'
import { partnerApiUtils } from 'core/service-wrapper/partner-api/handler-request/service-utils'
import { getIdentityUser, getUserAgentCode, getUserAgentType } from 'identity/selectors'
import {
  partnerCalculationFetched,
  partnerPolicyFetched,
  partnerPolicyFetching,
  partnerRequestFailure,
  partnerRequesting,
  partnerRequestSuccess,
  partnerUpdateFetching,
  patchAvailableDisplayProducts,
  patchAvailableRiders,
  setFnaConfiguration,
  updateCurrentSelectedProduct,
  updateFormState,
  updatePackageCanProcess,
} from 'quick-quote/actions'
import * as insuredInformation from 'quick-quote/insured-information/actions'
import {
  getAge,
  getBirthdate,
  getInsured,
  getPayerAge,
  getPayerBirthdate,
} from 'quick-quote/insured-information/selectors'
import { setNotificationMessage } from 'quick-quote/notification/actions'
import {
  updateCampaignDiscountEndDate,
  updateCampaignDiscountMessage,
} from 'quick-quote/product-common/coverage-plan/actions'
import * as productCommonCoveragePlan from 'quick-quote/product-common/coverage-plan/actions'
import {
  getAvailableRiders,
  getAvailableRidersByCode,
  getSelectedModelFactorID,
  getSelectedRiders,
  getSumAssured,
} from 'quick-quote/product-common/coverage-plan/selectors'
import {
  resetExpectedReturnComparisons,
  resetRPUDRExpectedReturnComparisons,
} from 'quick-quote/product-investment/coverage-plan/actions'
import * as productLifeProtectPlusCoveragePlanActions from 'quick-quote/product-life-protect-plus/actions'
import * as productInvestmentCoveragePlan from 'quick-quote/product-investment/coverage-plan/actions'
import { updateInvestmentBenefit } from 'quick-quote/product-life-save-pro/benefit-illustration/actions'
import * as productLoanCoveragePlan from 'quick-quote/product-loan/coverage-plan/redux'
import * as productMRTA from 'quick-quote/product-mrta/actions'
import { getRiderPlanName } from 'quick-quote/product-mrta/sagas'
import * as productSelection from 'quick-quote/product-selection/actions'
import {
  getSelectedDisplayProduct,
  getSelectedDisplayProductCode,
  getSelectedProductCategoryId,
} from 'quick-quote/product-selection/selectors'
import {
  updateBenefitSummary,
  updatePolicyValue,
  updateTaxDeduction,
} from 'quick-quote/product-whole-life/benefit-illustration/actions'
import * as productWholeLifeBenefit from 'quick-quote/product-whole-life/benefit-illustration/actions'
import {
  getPartnerAvailableDisplayProduct,
  getPartnerAvailableDisplayProductByCode,
  getPartnerAvailableRiderByDisplayProductCode,
  getPartnerPackageCanProcess,
  getPartnerCurrentSelectedProduct,
  getPartnerFormState,
} from 'quick-quote/selectors/partner-selector'
import { store as qqStore } from 'store'

/**
 * @param {ReturnType<import('core/service-wrapper/partner-api/service').createPartnerApiCache>} cache
 * @param {ReturnType<import('core/service-wrapper/partner-api/handler/insured-information').createInsuredInformationHandler>} insuredInformationHandler
 */
export function createProductCommonService(cache, insuredInformationHandler) {
  function getDisplayProductFromCache(code, options = { listAll: false }) {
    const { listAll } = options
    const products = getPartnerAvailableDisplayProductByCode(code)(qqStore.getState())
    if (listAll) {
      return products
    }
    return _.isUndefined(products) ? undefined : products.length === 1 ? products[0] : products
  }

  function getAvailableRidersByDisplayProductCode(code) {
    return getPartnerAvailableRiderByDisplayProductCode(code)(qqStore.getState())
  }

  function canCallRequestProductPackage(payload) {
    const invalidInsured = _.isEmpty(payload.insured.birthdate) || _.isEmpty(payload.insured.natureOfDutyCode)
    const invalidPayer =
      payload.payer &&
      (_.isEmpty(payload.payer.birthdate) ||
        _.isEmpty(payload.payer.natureOfDutyCode) ||
        _.isEmpty(payload.payer.relation))
    return !_.isEmpty(payload.packageCategory) && !_.isEmpty(payload.packageCode) && !invalidInsured && !invalidPayer
  }

  function canCallRequestCalculate(payload) {
    const invalidInsured = _.isEmpty(payload.insured.birthdate) || _.isEmpty(payload.insured.natureOfDutyCode)
    const invalidPayer =
      !!payload.payer &&
      (_.isEmpty(payload.payer.birthdate) ||
        _.isEmpty(payload.payer.natureOfDutyCode) ||
        _.isEmpty(payload.payer.relation))
    const invalidBasicPlan = _.isEmpty(payload.basicPlan.basicPlanCode) || _.isEmpty(payload.basicPlan.productRefName)

    return !invalidInsured && !invalidPayer && !invalidBasicPlan
  }

  function* getProductCategories() {
    const user = yield select(getIdentityUser)
    const agentType = yield select(getUserAgentType)
    const agentCode = yield select(getUserAgentCode)
    // const { ADVISORZONE_MIDDLEWARE } = getAppConfig()

    try {
      const result = yield partnerApiRequest.getProductCategories(user, agentType, agentCode)
      if (!result) {
        return undefined
      }
      const productCategories = result.data.productCategories

      // mapping product category and packages
      let displayProducts = {}
      let availablePlanCodes = []
      let availableCategories = []

      for (const productCategory of productCategories) {
        let hasSellableProduct = false

        productCategory.packages.forEach((item) => {
          // if (item.rightToSell === 'Y') {
          //   hasSellableProduct = true
          //   availablePlanCodes.push(...item.availableCodes)
          // }

          for (let planCode of item.availableCodes) {
            displayProducts[planCode] = {
              rightToSell: item.rightToSell,
              code: planCode,
              packageCode: item.packageCode,
              category: item.packageCategory,
              mainProduct: planCode === item.defaultPlanCode,
              displayName: item.title,
              description: item.description,
              benefits: item.benefits,
              image: item.packageImage,
              // image: new URL(`/partner-api/product/image/${item.packageImage}`, ADVISORZONE_MIDDLEWARE).toString(),
              ribbonMessage: item.ribbonText,
              requireLicenses: item.requireLicenses === 'Y',
              ordinaryProductForceLogin: item.ordinaryProductForceLogin === 'Y',
              payerSelection: item.payerSelection,
              allowUserType: item.agentUserTypes,
              modelFactors: item.modalFactors.map(partnerApiUtils.modalFactor.toModalFactorId),
              useLegacy: item.additionalFields.service_wrapper === 'legacy',
              useLegacySubmission: item.additionalFields.submission_wrapper === 'legacy',
              entryAge: {
                minimum: partnerApiHandlerHelper.convertAgeSymbol(item.productInsuredMinAge),
                maximum: partnerApiHandlerHelper.convertAgeSymbol(item.productInsuredMaxAge),
              },
              entrySumAssured: {
                minimum: item.productMinSumAssured,
                maximum: item.productMaxSumAssured,
              },
              paymentPeriod: partnerApiHandlerHelper.mappingPeriod(item.paymentPeriod),
              coveragePeriod: partnerApiHandlerHelper.mappingPeriod(item.coveragePeriod),
              pensionAge: '',
              licenses: item.licenses ?? [],
              riders: item.riders ?? [],
              requiredRiders: item.requiredRiders ?? [],
              optionalRiders: item.optionalRiders ?? [],
              aura: {},
              campaignDiscount: {},
              ..._.omit(item.additionalFields, ['service_wrapper']),
              ..._.omit(item.overrideFields, ['product_groups']),
              excludeMergeFieldKeys: [..._.keys(item.additionalFields), ..._.keys(item.overrideFields)]
            }
          }
        })

        if (hasSellableProduct && productCategory.disabled !== 'Y') {
          availableCategories.push(productCategory.productCategory)
        }
      }

      // default selected tab product category
      const viewConfiguration = partnerApiHandlerHelper.mappingToViewConfiguration(productCategories, agentType)

      return {
        viewConfiguration,
        availableCategories: _.uniq(availableCategories),
        availablePlanCodes: _.uniq(availablePlanCodes),
        displayProducts: Object.values(displayProducts),
      }
    } catch (e) {
      const message = partnerApiUtils.getMessageError(e)
      yield put(setNotificationMessage(message))
    }

    return {
      viewConfiguration: {},
      availableCategories: [],
      availablePlanCodes: [],
      displayProducts: [],
    }
  }

  function* getProductPackageBySelectedProduct(
    selectedProductCategoryId,
    selectedDisplayProductCode,
    comingDisplayProduct,
    needUpdateDefaultValue = true
  ) {
    yield delay(10)
    const user = yield select(getIdentityUser)

    const body = yield call(partnerApiHandlerHelper.createCalculatePayload, {
      excludeBasicPlan: true,
      excludeRider: true,
      excludeFund: true,
    })

    body.packageCategory = selectedProductCategoryId
    body.packageCode = selectedDisplayProductCode

    if (!canCallRequestProductPackage(body)) {
      return
    }

    try {
      const result = yield call(partnerApiRequest.getProductPackage, user, body)
      if (result === undefined) {
        return
      }

      const {
        displayProducts,
        unsellableProducts,
        defaultSelectProduct,
        ridersInProduct,
      } = partnerApiHandlerHelper.mappingToDisplayProducts(
        partnerApiUtils.birthdate.toFrontEnd(body.insured.birthdate),
        result.data,
        cache.showRiderUnsellable
      )

      // update cache displayProducts
      const currentSelectedProduct = yield select(getPartnerCurrentSelectedProduct)
      let previousCanProcess = yield select(getPartnerPackageCanProcess)
      let previousPackageCategory = currentSelectedProduct.packageCategory
      let previousPackageCode = currentSelectedProduct.packageCode

      const patch_product = (obj) => {
        if (!!obj.useLegacy && comingDisplayProduct) {
          obj.useLegacy = comingDisplayProduct.useLegacy
        }
      }

      let productGroups = _.groupBy(displayProducts.length ? displayProducts : unsellableProducts, 'code')
      for (const code in productGroups) {
        productGroups[code].forEach(patch_product)
      }

      yield put(updateCurrentSelectedProduct(selectedProductCategoryId, selectedDisplayProductCode))
      yield put(patchAvailableDisplayProducts(productGroups))
      yield put(patchAvailableRiders(ridersInProduct))
      yield put(updateFormState({ dirty: false }))

      if (displayProducts.length === 0) {
        yield put(updatePackageCanProcess(false))
        yield call(updateStateInsuredInformation, body, result.data.errors)

        yield put(productCommonCoveragePlan.updateSelectedPlan(''))
        yield put(productSelection.changeProduct({ ...comingDisplayProduct, ...defaultSelectProduct }))
        yield put(partnerRequestSuccess())

        return
      }

      yield put(updatePackageCanProcess(true))

      // find a new default when have been new get package when a same product
      // case back to edit insured information and click next to calculate must select previous plan
      let _defaultSelectProduct = defaultSelectProduct
      const selectedProduct = yield select(getSelectedDisplayProduct)
      const foundDefaultSelected = displayProducts.find(
        (item) =>
          item.productRefName === selectedProduct.productRefName && item.basicPlanCode === selectedProduct.basicPlanCode
      )
      if (foundDefaultSelected) {
        _defaultSelectProduct = foundDefaultSelected
      }

      // prevent loop from event selectProduct
      yield call(defaultSelectModalFactor, _defaultSelectProduct)

      let selectedPlan = ''
      const categoryRequiredSelectedPlan = [values.PA]
      if (categoryRequiredSelectedPlan.includes(_defaultSelectProduct.category) && _defaultSelectProduct.defaults) {
        selectedPlan = _defaultSelectProduct.defaults.code
      }

      let updateFresh = true
      if (previousPackageCategory !== selectedProductCategoryId || previousPackageCode !== selectedDisplayProductCode) {
        yield put(productCommonCoveragePlan.updateSelectedPlan(selectedPlan))
        yield put(productSelection.selectProduct({ ...comingDisplayProduct, ..._defaultSelectProduct }))
      } else if (
        previousPackageCategory !== selectedProductCategoryId ||
        previousPackageCode !== selectedDisplayProductCode ||
        selectedProduct?.code !== _defaultSelectProduct.code ||
        selectedProduct?.basicPlanCode !== _defaultSelectProduct.basicPlanCode ||
        selectedProduct?.productRefName !== _defaultSelectProduct.productRefName
      ) {
        yield put(productCommonCoveragePlan.updateSelectedPlan(selectedPlan))
        yield put(productSelection.changeProduct({ ...comingDisplayProduct, ..._defaultSelectProduct }))
      } else {
        updateFresh = false
      }

      // default value
      if ((needUpdateDefaultValue && updateFresh) || !previousCanProcess) {
        if (_defaultSelectProduct.category === biMessages.CATEGORY_INVESTMENT) {
          yield put(resetExpectedReturnComparisons())
          yield put(resetRPUDRExpectedReturnComparisons())

          if ([values.RPUL, values.RPUDR].includes(_defaultSelectProduct.code)) {
            yield put(productInvestmentCoveragePlan.updateRegularPremium(values.DEFAULT_RPP, []))
          } else {
            yield put(productInvestmentCoveragePlan.updateRegularPremium(0, []))
          }
        } else if (_defaultSelectProduct.category === biMessages.CATEGORY_LOAN) {
          yield put(productLoanCoveragePlan.updateSumAssured(_defaultSelectProduct.defaultSumAssured))
        } else if (_defaultSelectProduct.category === biMessages.CATEGORY_MRTA) {
          yield put(productCommonCoveragePlan.updateSumAssured(valid(_defaultSelectProduct.defaultSumAssured)))
          yield put(productMRTA.updateCoveragePeriod(_defaultSelectProduct.coveragePeriod.value))
          yield put(productMRTA.updateLoanPeriod(_defaultSelectProduct.coveragePeriod.value))
          yield put(productMRTA.updateLoanAmount(0))
        } else {
          yield put(productCommonCoveragePlan.updateSumAssured(valid(_defaultSelectProduct.defaultSumAssured)))
        }
      }

      if (needUpdateDefaultValue) {
        const comingAvailableRiders = yield call(getAvailableRiderByBasicPlanCode, _defaultSelectProduct, updateFresh)
        yield put(productCommonCoveragePlan.updateAvailableRiders(comingAvailableRiders))
      }

      // recalculate
      // calculateByPremium to true when ILP product for force set maxSA to sumAssured
      const isProductInvestment = comingDisplayProduct.category === biMessages.CATEGORY_INVESTMENT
      yield fork(
        calculateWithValidation,
        {},
        {
          calculateByPremium: needUpdateDefaultValue && isProductInvestment,
        }
      )
    } catch (e) {
      const message = partnerApiUtils.getMessageError(e)
      yield put(setNotificationMessage(message))
    }
  }

  function getRiderSelectedPlan(props) {
    const { riderInGroups, riderTarget } = props
    if (riderInGroups?.length === 0) {
      return {}
    }

    if (
      riderHandler[riderTarget.riderGroup] &&
      typeof riderHandler[riderTarget.riderGroup].selectedPlan === 'function'
    ) {
      return riderHandler[riderTarget.riderGroup].selectedPlan(props)
    } else {
      return riderHandler.selectedPlan(props)
    }
  }

  function* getAvailableRiderByBasicPlanCode(displayProduct, newState) {
    const cacheRiders = yield select(getPartnerAvailableRiderByDisplayProductCode(displayProduct.code))
    if (cacheRiders && Object.values(cacheRiders).length) {
      const states = {}
      const availableRidersInGroup = {}

      const cacheRidersList = Object.values(cacheRiders)

      for (const rider of cacheRidersList) {
        if (rider.riderGroup in states) {
          continue
        }

        let riderInGroups = cacheRidersList.filter(
          (item) => item.rightToSell === 'Y' && item.rightToBuy === 'Y' && rider.riderGroup === item.riderGroup
        )
        let defaultSelected

        if (displayProduct.category === biMessages.CATEGORY_MRTA) {
          const mrtappCode = getRiderPlanName(displayProduct.code, displayProduct.coveragePeriod.value)
          defaultSelected = riderInGroups.find(
            (item) => item.rightToSell === 'Y' && item.rightToBuy === 'Y' && item.riderCode === mrtappCode
          )
        } else {
          defaultSelected = riderInGroups.find(
            (item) => item.rightToSell === 'Y' && item.rightToBuy === 'Y' && item.defaultSelected
          )
        }

        if (!defaultSelected) {
          if (riderInGroups.length) {
            defaultSelected = riderInGroups[0]
          } else {
            riderInGroups = cacheRidersList.filter((item) => rider.riderGroup === item.riderGroup)
            defaultSelected = riderInGroups.length ? riderInGroups[0] : undefined
          }
        }

        let riderOptions = {}
        if (riderHandler[rider.riderGroup] && typeof riderHandler[rider.riderGroup].createOption === 'function') {
          riderOptions = riderHandler[rider.riderGroup].createOption(riderInGroups, rider)
        }

        let selectedPlan = getRiderSelectedPlan({
          riderInGroups,
          riderTarget: defaultSelected,
          riderState: riderOptions,
        })

        let sumAssured = defaultSelected.defaultSA
        if (defaultSelected.fixedSA === 'Y') {
          sumAssured = defaultSelected.minSA
        }

        availableRidersInGroup[rider.riderGroup] = riderInGroups.map((item) => item.riderCode)
        states[rider.riderGroup] = {
          code: rider.riderGroup,
          coveragePeriod: partnerApiHandlerHelper.mappingPeriod(rider.coveragePeriod),
          payerRelation: rider.payerSelection,
          prerequisites: rider.constraints.mandatoryRiders,
          incompatibilities: rider.constraints.exceptionalRiders,
          aura: {
            AURA_PRODUCT_CODE: rider.auraProductCode,
            INPUT_XML_QUESTION_FILTERS: rider.auraInputXMLQuestionFilter,
          },
          isSelected: displayProduct.selectedRiders.includes(rider.riderGroup),
          isSelectable: riderInGroups.some((item) => item.rightToSell === 'Y' && item.rightToBuy === 'Y'),
          visible: rider.visible,
          yearsOfCoverage: -1,
          yearsOfPayment: -1,
          errors: Object.entries(rider.errorMap)
            .filter(([ruleName, _]) => ruleName !== 'PayerRequireRule')
            .map(([ruleName, error]) => ({
              ruleName,
              type: error.validationType,
              message: error.message,
            })),
          sumAssured: sumAssured,
          premium: 0,
          fullBasicPremium: 0,
          fullAnnualBasicPremium: 0,
          defaultSumAssured: defaultSelected.defaultSA,
          selectedPlan: selectedPlan,
          ...riderOptions,
          ...rider.overrideFields,
        }
      }

      // return states
      return yield call(mergeRiderState, states, newState, availableRidersInGroup, cacheRidersList)
    }
    return []
  }

  function* mergeRiderState(riders, newState, availableRidersInGroup, cacheRiders) {
    const newStates = {}
    const riderStates = yield select(getAvailableRiders)

    for (const [, rider] of Object.entries(riders)) {
      const riderState = riderStates.find((r) => r.code === rider.code)
      if (!newState && riderState) {
        // handle on case same rider but not have an option
        const currentSelectedPlan = riderState.selectedPlan?.fullPlanCode?.length
          ? riderState.selectedPlan.fullPlanCode
          : riderState.selectedPlan?.planCode?.length
          ? riderState.selectedPlan.planCode
          : riderState.code
        const hasInRiderGroup = availableRidersInGroup[rider.code].includes(currentSelectedPlan)
        if (hasInRiderGroup) {
          const selectedRiderPlan = cacheRiders.find((item) => item.riderCode === currentSelectedPlan)
          const isFixedSumAssured = selectedRiderPlan.fixedSA === 'Y'
          const sumAssured = isFixedSumAssured ? selectedRiderPlan.minSA : selectedRiderPlan.defaultSA
          const usesDefaultSumAssured = riderState.sumAssured === riderState.defaultSumAssured

          newStates[rider.code] = {
            ...rider,
            selectedPlan: riderState.selectedPlan,
            sumAssured: isFixedSumAssured || usesDefaultSumAssured ? sumAssured : riderState.sumAssured,
            defaultSumAssured: selectedRiderPlan.defaultSA,
            isSelected: riderState.isSelected,
          }
        } else {
          newStates[rider.code] = _.cloneDeep(rider)
        }
      } else {
        newStates[rider.code] = _.cloneDeep(rider)
      }
    }

    return newStates
  }

  function* getFnaConfiguration() {
    const body = yield partnerApiRequest.getFnaConfiguration()
    yield put(setFnaConfiguration(_.omit(body.data, 'distributionChannel')))
  }

  async function getBasicPlan(displayProductQuery) {
    const products = getDisplayProductFromCache(displayProductQuery.code)
    if (Array.isArray(products)) {
      const defaultProduct = products.filter((p) => p.basicPlanCode === p.defaults.code)
      if (defaultProduct?.length) {
        return defaultProduct[0]
      }
      return products[0]
    }
    return products
  }

  function* createCalculationPayload(props, options) {
    const calReq = yield call(
      partnerApiHandlerHelper.createCalculatePayload,
      {
        calculateByPension: options.calculateByPension || false,
        calculateByPremium: options.calculateByPremium || false,
      },
      cache
    )
    if (!_.isUndefined(props.natureOfDuty)) calReq.insured.natureOfDutyCode = props.natureOfDuty
    if (!_.isUndefined(props.modelFactor))
      calReq.basicPlan.modalFactor = partnerApiUtils.modalFactor.toSymbol(props.modelFactor)
    if (!_.isUndefined(props.basicPremium)) calReq.basicPlan.basicPremium = props.basicPremium
    if (!_.isUndefined(props.sumAssured)) calReq.basicPlan.basicSA = props.sumAssured
    if (!_.isUndefined(props.RPP)) calReq.basicPlan.RPP = props.RPP
    if (!_.isUndefined(props.RTU)) calReq.basicPlan.RTU = props.RTU
    if (!_.isUndefined(props.LSTU)) calReq.basicPlan.LSTU = props.LSTU

    calReq.setting = {
      skipValidateFund: !cache.route_state.fund_allocation,
      showInvestmentBenefitTableAsDisplay: true,
      skipGeneratePolicy: options.skipGeneratePolicy, // !cache.route_state.coverage_plan
    }

    return calReq
  }

  function isGeneratePolicySlowly(selectedDisplayProduct) {
    return ['RPUDR', 'RPUL', 'SPUL'].includes(selectedDisplayProduct.code)
  }

  function* getPlanBoosterSA(productCode) {
    switch (productCode) {
      case values.LIFEPROTECT_PLUS_CODE.LIFEPROTECT_PLUS_9_UNDER:
      case values.LIFEPROTECT_PLUS_CODE.LIFEPROTECT_PLUS_19_UNDER:
      case values.LIFEPROTECT_PLUS_CODE.LIFEPROTECT_PLUS_99_UNDER:
        return values.BOOSTER_SA_PLAN.FIFTY_PERCENT
      case values.LIFEPROTECT_PLUS_CODE.LIFEPROTECT_PLUS_9_HIGHER:
      case values.LIFEPROTECT_PLUS_CODE.LIFEPROTECT_PLUS_19_HIGHER:
      case values.LIFEPROTECT_PLUS_CODE.LIFEPROTECT_PLUS_99_HIGHER:
      case values.COMBO_CI_LIFEPROTECT_PLUS_CODE.WLF19H:
      case values.COMBO_CI_LIFEPROTECT_PLUS_CODE.WLF99H:
      case values.COMBO_HEALTH_ESSENTIAL_LIFEPROTECT_PLUS_CODE.WLF19H:
      case values.COMBO_HEALTH_ESSENTIAL_LIFEPROTECT_PLUS_CODE.WLF99H:
        return values.BOOSTER_SA_PLAN.ONE_HUNDRED_PERCENT
      default:
        return values.BOOSTER_SA_PLAN.ONE_HUNDRED_PERCENT
    }
  }

  function* calculateWithValidation(props = {}, options = {}) {
    yield delay(10)
    const selectedDisplayProduct = yield select(getSelectedDisplayProduct)

    const user = yield select(getIdentityUser)

    const calReq = yield call(createCalculationPayload, props, {
      ...options,
      skipGeneratePolicy: isGeneratePolicySlowly(selectedDisplayProduct) || cache.flag.skipGeneratePolicy,
    })
    if (!canCallRequestCalculate(calReq)) {
      return
    }

    try {
      yield put(partnerUpdateFetching(true, !calReq.setting.skipGeneratePolicy))

      const formState = yield select(getPartnerFormState)
      const isProductInvestment = selectedDisplayProduct.category === biMessages.CATEGORY_INVESTMENT

      let calRes = undefined
      let policyValue = undefined

      const response = yield call(partnerApiRequest.calculateWithPolicyValue, user, calReq)
      if (!response) {
        // case cancel request
        return
      }

      yield put(partnerCalculationFetched())

      calRes = response.data.calculation
      policyValue = response.data.policy

      // update state
      if (isProductInvestment) {
        // do nothing
      } else {
        yield put(productCommonCoveragePlan.updateBasicPremium(calRes.basicPlan.basicPremium))
        yield put(productCommonCoveragePlan.updatFullAnnualBasicPremium(calRes.basicPlan.basicAnnualPremium))

        if (selectedDisplayProduct.category === biMessages.CATEGORY_LIFE_PROTECT_PLUS) {
          const boosterMessage = calRes.basicPlan.boosterMessage || ''
          const boosterRemark = calRes.basicPlan.boosterRemark || ''
          const boosterPlan = yield call(getPlanBoosterSA, calRes.basicPlan.basicPlanCode)
          yield put(
            productLifeProtectPlusCoveragePlanActions.updateBoosterSA({ boosterMessage, boosterRemark, boosterPlan })
          )
        }
      }

      let totalPremium = valid(calRes.totalPremium)
      let totalRiderPremium = 0

      yield call(updateStateInsuredInformation, calReq, calRes.basicPlan.errorMap)
      yield call(updateStateProductBasic, calRes.basicPlan, calReq, { ...props, ...options, selectedDisplayProduct })

      // update rider
      const insured = yield select(getInsured)
      const availableRider = _.cloneDeep(yield select(getAvailableRidersByCode))
      const selectedRiders = _.mapKeys(_.cloneDeep(yield select(getSelectedRiders)), (item) => item.code)
      const riderCaches = yield select(getPartnerAvailableRiderByDisplayProductCode(selectedDisplayProduct.code))
      let updateRiderStates = {}

      for (const [code, riderState] of Object.entries(availableRider)) {
        const riderCode = riderHandler.getRiderSelectedPlanCode(riderState)
        if (!riderCaches || !riderCaches[riderCode]) continue

        const riderInGroups = Object.values(riderCaches).filter((it) => it.riderGroup === riderState.code)
        const riderCal = calRes.riders.find((it) => it.riderCode === riderCode)
        const otherRiderCal = calRes.otherRiders.find((it) => riderInGroups.some((g) => it.riderCode === g.riderCode))

        totalRiderPremium += riderCal?.riderPremium ?? 0
        const [riderGroup, updateSate] = yield call(updateStateProductRider, {
          selectedRiders,
          riderState: riderState,
          riderCacheMap: riderCaches,
          riderInGroups,
          riderCache: riderCaches[riderCode],
          riderCal: riderCal,
          otherRiderCal: otherRiderCal,
          insured,
        })

        updateRiderStates[riderGroup] = updateSate

        availableRider[code] = { ...availableRider[code], ...updateSate }
        if (selectedRiders[code] && !updateSate.isSelected) {
          delete selectedRiders[code]
        }
      }

      for (const [code, riderState] of Object.entries(availableRider)) {
        const riderCode = riderHandler.getRiderSelectedPlanCode(riderState)

        if (!riderCaches || !riderCaches[riderCode]) continue

        updateRiderStates[code] = yield call(updateRiderPrerequisiteAndIncompatibility, {
          riderState,
          riderCacheMap: riderCaches,
          selectedRiders,
          riderCache: riderCaches[riderCode],
          updateState: updateRiderStates[code],
        })
      }

      yield Object.entries(updateRiderStates).map(([riderGroup, obj]) => {
        return put(productCommonCoveragePlan.updateRiderState(riderGroup, obj))
      })

      if ('TotalPremiumRule' in calRes.basicPlan.errorMap) {
        totalPremium = createCheckInvalid(calRes.totalPremium, [calRes.basicPlan.errorMap['TotalPremiumRule']])
      }

      yield put(productCommonCoveragePlan.updateTotalPremium(totalPremium))
      yield put(productCommonCoveragePlan.updateTotalRiderPremium(totalRiderPremium))

      // verify current state is same previous state
      // issue:
      // - when rider have error state change to unselected automatically (request not include rider to cal with selected)
      if (calReq.riders) {
        yield delay(50)
        const newCalReq = yield call(
          createCalculationPayload,
          {},
          { skipGeneratePolicy: calReq.setting.skipGeneratePolicy }
        )

        const riderStringify = JSON.stringify(calReq.riders.map((r) => r.riderCode))
        const newRiderStringify = JSON.stringify(newCalReq.riders.map((r) => r.riderCode))
        if (riderStringify !== newRiderStringify) {
          yield fork(calculateWithValidation)
          return
        }
      }

      // update policy
      if (!cache.flag.skipGeneratePolicy) {
        yield call(updateRiderBenefit)
        yield fork(updatePolicy, calReq, calRes, policyValue)
      }

      yield put(partnerRequestSuccess())
    } catch (e) {
      yield put(partnerRequestFailure())
      const message = partnerApiUtils.getMessageError(e)
      yield put(setNotificationMessage(message))
    }
  }

  function* updateRiderPrerequisiteAndIncompatibility(props) {
    const { riderState, riderCacheMap, selectedRiders, riderCache, updateState } = props

    if (riderState.prerequisites.length) {
      const prerequisitesGroups = _.uniq(
        riderState.prerequisites
          .filter((riderCode) => riderCacheMap[riderCode])
          .map((riderCode) => riderCacheMap[riderCode].riderGroup)
      )
      const selectedGroups = Object.values(selectedRiders).filter(
        (selected) => prerequisitesGroups.indexOf(selected.code) !== -1
      )
      if (prerequisitesGroups.length !== selectedGroups.length) {
        for (const riderGroup of prerequisitesGroups) {
          updateState.errors.push({
            ruleName: 'RULE_RIDER_PREREQUISITE',
            type: values.VALIDATION,
            message: riderCache.validateMessage.prerequisites[riderGroup],
          })
        }
      }
    }

    let isSelectable = true
    if (riderState.incompatibilities.length) {
      const incompatibilitiesGroups = _.uniq(
        riderState.incompatibilities
          .filter((riderCode) => riderCacheMap[riderCode])
          .map((riderCode) => riderCacheMap[riderCode].riderGroup)
      )
      const incompatibilitiesSelected = Object.values(selectedRiders).filter(
        (selected) => incompatibilitiesGroups.indexOf(selected.code) !== -1
      )
      if (incompatibilitiesSelected.length) {
        const incompatibilitiesMessage = Mustache.render(MESSAGES.RULE_RIDER_INCOMPATIBILITY, {
          riderName: riderState.name,
          riderIncompatibilities: incompatibilitiesSelected.map((item) => item.name).join(', '),
        })

        updateState.errors.push({
          ruleName: 'RULE_RIDER_INCOMPATIBILITY',
          type: values.ELIGIBILITY,
          message: incompatibilitiesMessage,
        })
      }
      isSelectable = incompatibilitiesSelected.length === 0
    }

    if (updateState.isSelectable) {
      updateState.isSelectable = isSelectable
    }

    return updateState
  }

  function* updateRiderBenefit() {
    try {
      const riders = yield select(getAvailableRiders)
      const selectedRiders = riders.filter(({ isSelected, isSelectable }) => isSelected && isSelectable)
      const nonSelectedRiders = riders.filter(({ isSelected, isSelectable }) => !(isSelected && isSelectable))

      if (selectedRiders.length > 0) {
        const age = yield select(getAge)
        yield selectedRiders.map((rider) => {
          const riderBenefitData = generateRiderBenefitData(
            {
              age,
              riderBenefitData: cache.riderBenefits.find((item) => item.riderCode === rider.code),
              riderSumAssured: rider.sumAssured,
              selectedRiders: selectedRiders,
            },
            rider.code
          )

          return put(productWholeLifeBenefit.addRiderBenefit(rider.code, riderBenefitData))
        })
      }

      yield nonSelectedRiders.map((rider) => {
        return put(productWholeLifeBenefit.removeRiderBenefit(rider.code))
      })
    } catch (error) {
      console.error(error)
    }
  }

  function* updatePolicy(calReq, calRes, policyValue) {
    const selectedDisplayProduct = yield select(getSelectedDisplayProduct)
    if (calReq.setting.skipGeneratePolicy) {
      const user = yield select(getIdentityUser)
      calReq.setting.skipGeneratePolicy = false

      yield put(partnerPolicyFetching())

      const response = yield call(partnerApiRequest.calculateWithPolicyValue, user, calReq)
      if (!response) {
        // case cancel request
        return
      }

      calRes = response.data.calculation
      policyValue = response.data.policy
    }

    yield put(partnerPolicyFetched())

    if (!policyValue) {
      return
    }

    const code = selectedDisplayProduct.code
    const basicPlanCode = selectedDisplayProduct.basicPlanCode

    /**
     * @param code1
     * @param code2
     * @param funcName {keyof policy_basic['DEFAULT']}
     * @returns {*[]|*}
     */
    const get_fun = (code1, code2, funcName) => {
      if (policy_basic[code1] && policy_basic[code1][funcName]) {
        return policy_basic[code1][funcName]
      }
      if (policy_basic[code2] && policy_basic[code2][funcName]) {
        return policy_basic[code2][funcName]
      }
      return policy_basic['DEFAULT'][funcName]
    }

    const policy_mapper = get_fun(code, basicPlanCode, 'mapping_policy')
    const benefit_summary_mapper = get_fun(code, basicPlanCode, 'mapping_benefit_summary')
    const tax_deduction_mapper = get_fun(code, basicPlanCode, 'mapping_tax_deduction')
    const investment_benefit_mapper = get_fun(code, basicPlanCode, 'mapping_investment_benefit')

    const comingPolicyValue = policy_mapper(calRes, policyValue)
    const comingBenefitSummary = benefit_summary_mapper(policyValue)
    const comingTaxDeduction = tax_deduction_mapper(policyValue)

    yield put(updatePolicyValue(comingPolicyValue))
    yield put(updateBenefitSummary(comingBenefitSummary))
    yield put(updateTaxDeduction(comingTaxDeduction))

    if (investment_benefit_mapper !== undefined) {
      const investmentBenefit = investment_benefit_mapper(policyValue, basicPlanCode)
      if (investmentBenefit) {
        yield put(updateInvestmentBenefit(investmentBenefit))
      }
    }

    if (selectedDisplayProduct.category === values.INVESTMENT) {
      const expectedReturn = policy_basic[code].mapping_expected_return(policyValue)
      if (code === values.RPUDR) {
        yield put(productInvestmentCoveragePlan.updateRPUDRExpectedReturnComparisons(expectedReturn))
      } else {
        yield put(productInvestmentCoveragePlan.updateExpectedReturnComparisons(expectedReturn))
      }
    }
  }

  function createCheckInvalid(value, errors) {
    let invalidValue = createChecker([])({}, value)

    for (const error of errors) {
      invalidValue.addMessage({ type: error.validationType, message: error.message })
    }

    return invalidValue
  }

  function* updateStateInsuredInformation(calRequest, errors) {
    const extractErrorMessage = (error) => {
      return typeof error === 'string' ? error : error.message
    }

    const currentDate = moment().format('DD/MM/YYYY')
    const selectedDisplayProduct = yield select(getSelectedDisplayProduct)

    // insured
    let birthdateState = yield select(getBirthdate)
    let birthdateFE = partnerApiUtils.birthdate.toFrontEnd(calRequest.insured.birthdate)
    if (partnerApiUtils.birthdate.isEquals(birthdateState, birthdateFE)) {
      const previousAge = yield select(getAge)
      const validateResult = _validateBirthdate(
        birthdateFE,
        selectedDisplayProduct.entryAge,
        previousAge,
        currentDate,
        false
      )
      const haveError = 'InsuredAgeRule' in errors
      let error = []
      if (haveError) {
        error = [extractErrorMessage(errors['InsuredAgeRule'])]
      }

      // prettier-ignore
      yield call(
        insuredInformationHandler.insured.patchEditBirthdate,
        { birthdate: birthdateFE, forceUpdate: true },
        insuredInformation.patchBirthdate,
        { ...validateResult, error: error },
      )

      if (haveError) {
        yield put(insuredInformation.resetOccupation())
        yield put(insuredInformation.clearPayerNeeded())
      }
    }

    // payer
    if (calRequest.payer) {
      birthdateState = yield select(getPayerBirthdate)
      birthdateFE = partnerApiUtils.birthdate.toFrontEnd(calRequest.payer.birthdate)
      if (partnerApiUtils.birthdate.isEquals(birthdateState, birthdateFE)) {
        const ageState = yield select(getPayerAge)
        const validateResult = _validateBirthdate(
          birthdateFE,
          DEFAULT.DEFAULT_PAYER_ENTRY_AGE,
          ageState,
          currentDate,
          true
        )

        // prettier-ignore
        yield call(
          insuredInformationHandler.payer.patchBirthdate,
          { birthdate: birthdateFE },
          insuredInformation.patchPayerBirthdate,
          { ...validateResult, error: 'PayerAgeRule' in errors ? [extractErrorMessage(errors['PayerAgeRule'])] : [] }
        )
      }
    }

    // occupation
    let occupationInsuredErrors = []
    if ('InvalidOccupationFactorRule' in errors) {
      occupationInsuredErrors.push('อาชีพของผู้เอาประกันภัยไม่สามารถซื้อแบบประกันภัยนี้ได้')
      // occupationInsuredErrors.push(errors['InvalidOccupationFactorRule'])
    } else if ('OccupationFactorRule' in errors) {
      occupationInsuredErrors.push('อาชีพของผู้เอาประกันภัยไม่สามารถซื้อแบบประกันภัยนี้ได้')
      // occupationInsuredErrors.push(errors['OccupationFactorRule'])
    }
    yield put(insuredInformation.updateOccupationErrors(occupationInsuredErrors))
  }

  function* updateStateProductBasic(basicPlan, calReq, calProps) {
    // skip update state product base when have error insured or payer
    if (
      'InsuredAgeRule' in basicPlan.errorMap ||
      'PayerNeedRule' in basicPlan.errorMap ||
      'PayerRelationRule' in basicPlan.errorMap ||
      'PayerRule' in basicPlan.errorMap ||
      'InvalidOccupationFactorRule' in basicPlan.errorMap ||
      'OccupationFactorRule' in basicPlan.errorMap
    ) {
      return
    }

    const basicPlanError = basicPlan.errorMap
    let sumAssuredState = valid(basicPlan.basicSA)
    sumAssuredState.isOk = basicPlan.basicSA !== 0
    let sumAssuredErrors = []

    if ('BasicSumAssuredRule' in basicPlanError) {
      sumAssuredErrors.push(basicPlanError['BasicSumAssuredRule'])
    }
    if ('LoanSumAssuredRule' in basicPlanError) {
      sumAssuredErrors.push(basicPlanError['LoanSumAssuredRule'])
    }
    if ('LoanAmountMRTARule' in basicPlanError) {
      sumAssuredErrors.push(basicPlanError['LoanAmountMRTARule'])
    }
    if ('SumAssuredContractMRTARule' in basicPlanError) {
      sumAssuredErrors.push(basicPlanError['SumAssuredContractMRTARule'])
    }
    if ('CoverageTermMRTARule' in basicPlanError) {
      sumAssuredErrors.push({
        ...basicPlanError['CoverageTermMRTARule'],
        validationType: 'VALIDATION_NOTSHOW',
      })
    }
    if ('InterestRateRule' in basicPlanError) {
      sumAssuredErrors.push({
        ...basicPlanError['InterestRateRule'],
        validationType: 'VALIDATION_NOTSHOW',
      })
    }

    if (sumAssuredErrors.length) {
      sumAssuredState = createCheckInvalid(basicPlan.basicSA, sumAssuredErrors)
    }

    yield put(productCommonCoveragePlan.updateOccupationFactor(transformOccupationFactor(basicPlan.occupationFactor)))

    if (calProps.selectedDisplayProduct.category === biMessages.CATEGORY_INVESTMENT) {
      const RTU = calProps.RTU ?? calReq.basicPlan.RTU
      const LSTU = calProps.LSTU ?? calReq.basicPlan.LSTU

      // product investment
      let regularPremiumErrors = []
      let regularTopUpErrors = []
      let lumpSumTopUpErrors = []

      if ('RegularPremiumMinimumRule' in basicPlanError) {
        regularPremiumErrors.push(basicPlanError['RegularPremiumMinimumRule'])
      }
      if ('RegularTopUpMaximumRule' in basicPlanError) {
        regularTopUpErrors.push(basicPlanError['RegularTopUpMaximumRule'])
      }
      if ('LumpSumTopUpMinimumRule' in basicPlanError) {
        lumpSumTopUpErrors.push(basicPlanError['LumpSumTopUpMinimumRule'])
      }

      const regularPremiumInvalid = createCheckInvalid(0, regularPremiumErrors)
      const regularTopUpInvalid = createCheckInvalid(0, regularTopUpErrors)
      const lumpSumTopUpInvalid = createCheckInvalid(0, lumpSumTopUpErrors)

      const formState = yield select(getPartnerFormState)

      yield put(
        productInvestmentCoveragePlan.updateRegularPremium(
          basicPlan.basicPremium,
          formState.dirty ? regularPremiumInvalid.getAllMessages() : []
        )
      )
      yield put(productInvestmentCoveragePlan.updateRegularTopUp(RTU ?? 0, regularTopUpInvalid.getAllMessages()))
      yield put(
        productInvestmentCoveragePlan.updateLumpSums({
          values: [
            {
              year: 1,
              value: LSTU ?? 0,
              isValid: lumpSumTopUpInvalid.isOk,
            },
          ],
          errors: lumpSumTopUpInvalid.getErrorMessages(),
        })
      )

      // sumAssured
      let sumAssured
      if (calProps.calculateByPremium || calProps.RPP) {
        sumAssured = basicPlan.maxSA
      } else {
        sumAssured = calReq.basicPlan.basicSA
      }
      const messageKey =
        basicPlan.minSA === basicPlan.maxSA ? 'RULE_IWEALTHY_EXPECTED_SUM_ASSURED' : 'RULE_IWEALTHY_SUM_ASSURED_LIMIT'
      const messages = sumAssuredErrors.length
        ? [{ type: sumAssuredErrors[0].validationType, message: sumAssuredErrors[0].message }]
        : [
            {
              type: values.ADDITIONAL_INFORMATION,
              message: Mustache.render(MESSAGES[messageKey], {
                minSumAssured: formatNumber(basicPlan.minSA),
                maxSumAssured: formatNumber(basicPlan.maxSA),
              }),
            },
          ]

      if (calProps.RPP) {
        if (basicPlan.basicSA < basicPlan.minSA || basicPlan.basicSA > basicPlan.maxSA) {
          yield fork(calculateWithValidation, { sumAssured: basicPlan.maxSA })
        } else {
          yield put(productInvestmentCoveragePlan.updateSumAssured(sumAssured, messages))
        }
      } else {
        yield put(productInvestmentCoveragePlan.updateSumAssured(sumAssured, messages))
      }

      return
    } else if (
      calProps.selectedDisplayProduct.category === biMessages.CATEGORY_MRTA ||
      calProps.selectedDisplayProduct.category === biMessages.CATEGORY_LOAN
    ) {
      const coverageTermErrors = []
      const interestRateErrors = []

      if ('CoverageTermMRTARule' in basicPlanError) {
        coverageTermErrors.push(basicPlanError['CoverageTermMRTARule'])
      }
      if ('InterestRateRule' in basicPlanError) {
        interestRateErrors.push(basicPlanError['InterestRateRule'])
      }

      const coverageTermInvalid = createCheckInvalid(calReq.basicPlan.coverageTerm, coverageTermErrors)
      const interestRateInvalid = createCheckInvalid(calReq.basicPlan.interestRate, interestRateErrors)

      if (calProps.selectedDisplayProduct.category === biMessages.CATEGORY_MRTA) {
        yield put(productCommonCoveragePlan.updateSumAssured(sumAssuredState))
        yield put(productMRTA.updateValidateCoveragePeriodErros(coverageTermInvalid))
        yield put(productMRTA.updateLoanAmount(calReq.basicPlan.loanAmount))
        yield put(productMRTA.editMRTASumAssuredContract(basicPlan.sumAssuredContract))
        yield put(productMRTA.updateValidateInterestRateErros(interestRateInvalid))
      } else {
        // TODO this not full implement for loan product
        yield put(productLoanCoveragePlan.updateSumAssuredErrors(sumAssuredErrors))
        yield put(productLoanCoveragePlan.updateCoverageTermErrors(coverageTermInvalid.getErrorMessages()))
      }
    } else {
      // product non-investment
      yield put(productCommonCoveragePlan.updateSumAssured(sumAssuredState))
      yield put(productCommonCoveragePlan.updateBasicPremium(basicPlan.basicPremium))
      yield put(productCommonCoveragePlan.updatFullAnnualBasicPremium(basicPlan.basicAnnualPremium))
    }

    if (_.isUndefined(calProps.basicPremium)) {
      yield put(productCommonCoveragePlan.updateBasicPremiumState(-1))
    }

    // support GLTSP change planCode by sumAssuredContract
    if (!_.isUndefined(basicPlan.updatePlanCode)) {
      const availableProducts = yield select(
        getPartnerAvailableDisplayProductByCode(calProps.selectedDisplayProduct.code)
      )
      if (availableProducts) {
        const comingDisplayProduct = availableProducts.find(
          (product) => product.basicPlanCode === basicPlan.updatePlanCode
        )
        if (comingDisplayProduct && comingDisplayProduct.basicPlanCode !== basicPlan.basicPlanCode) {
          yield put(productSelection.changeProduct(comingDisplayProduct))
          yield fork(calculateWithValidation)
        }
      }
    }

    // TODO implement this when partner-api has campaign feature
    // reset campaign discount message and date
    yield put(updateCampaignDiscountMessage([]))
    yield put(updateCampaignDiscountEndDate(''))
  }

  function* updateStateProductRider(props) {
    const { riderState, riderInGroups, riderCache, riderCal, otherRiderCal, insured } = props
    const updateCalObj = riderCal
      ? {
          premium: riderCal.riderPremium,
          premiumStructure: riderCal.premiumStructure,
          yearsOfCoverage: riderCal.yearOfCoverage,
          yearsOfPayment: riderCal.yearOfPayment,
          occupationFactor: transformOccupationFactor(riderCal.occupationFactor),
          sumAssured: riderCal.riderSA,
        }
      : undefined
    let errors = {}

    if (riderCache.rightToBuy === 'N') {
      riderState.errors
        .filter((it) => it.ruleName !== 'RULE_RIDER_PREREQUISITE' && it.ruleName !== 'RULE_RIDER_INCOMPATIBILITY')
        .forEach((it) => {
          errors[it.ruleName] = it
        })
    }

    // error from api
    const errorMap = riderCal?.errorMap ?? otherRiderCal?.errorMap ?? {}
    if (Object.keys(errorMap).length) {
      for (let [ruleName, error] of Object.entries(errorMap)) {
        if (!ruleName.startsWith('rider_prerequisite')) {
          errors[ruleName] = {
            ruleName,
            type: error.validationType,
            message: error.message,
          }
        }
      }
    }

    let hasEligibilityError = Object.values(errors).some((it) => it.type === values.ELIGIBILITY)
    const buyable = riderInGroups.some((item) => item.rightToSell === 'Y' && item.rightToBuy === 'Y')
    const isSelectable = buyable && !hasEligibilityError

    // support subRider to selectedPlanGroup
    if (riderCal?.subRiders?.length) {
      const subRiderCodes = riderCal.subRiders.map((subRider) => subRider.riderCode)
      let packageRiders = {}
      if (riderState.packageRiders) {
        for (const packageRider of riderState.packageRiders) {
          if (packageRider.allowAge && !isAgeAllowed(insured.age.value, packageRider.allowAge)) {
            continue
          }

          if (packageRider.planGroups) {
            packageRiders = packageRider.planGroups
              .filter((planGroup) => subRiderCodes.includes(planGroup.code))
              .reduce((acc, planGroup) => {
                acc[planGroup.code] = planGroup
                return acc
              }, {})
          }
        }

        updateCalObj.selectedPlanGroup = {}
        riderCal.subRiders.forEach((subRider) => {
          updateCalObj.selectedPlanGroup[subRider.riderCode] = {
            ...packageRiders[subRider.riderCode],
            riderCode: subRider.riderCode,
            riderSumAssured: subRider.riderSA,
            riderPremium: subRider.riderPremium,
          }
        })
      }
    }

    return [
      riderCache.riderGroup,
      {
        ...updateCalObj,
        isSelected: riderState.isSelected,
        isSelectable: isSelectable,
        errors: Object.values(errors),
      },
    ]
  }

  // ///////////////////////////////////////////////////////////////////////////
  // Event
  // ///////////////////////////////////////////////////////////////////////////

  function* onSelectProduct(comingDisplayProduct) {
    const packageCode = comingDisplayProduct.packageCode
    const selectedProductCategoryId = yield select(getSelectedProductCategoryId)
    yield put(partnerRequesting('change_product'))

    yield call(getProductPackageBySelectedProduct, selectedProductCategoryId, packageCode, comingDisplayProduct)
  }

  function* onUpdateSelectedBasicPlan(action) {
    const comingDisplayProduct = getDisplayProductFromCache(action.payload)
    if (comingDisplayProduct) {
      yield put(partnerRequesting('change_product'))
      yield call(onChangeProduct, comingDisplayProduct)
    }
  }

  function* onChangeProduct(comingDisplayProduct) {
    const previousDisplayProduct = yield select(getSelectedDisplayProduct)
    let updateComingDisplayProduct = comingDisplayProduct

    const comingProduct = yield call(findAvailableInProductGroup, { selectedDisplayProduct: comingDisplayProduct })
    if (comingProduct) {
      updateComingDisplayProduct = comingProduct
    }

    // update select modal factor
    yield call(defaultSelectModalFactor, updateComingDisplayProduct)

    const isProductChanged =
      previousDisplayProduct.code === '' || previousDisplayProduct.code !== updateComingDisplayProduct.code

    if (isProductChanged) {
      yield put(
        productCommonCoveragePlan.updateSumAssured(
          valid(updateComingDisplayProduct.defaultSumAssured ?? updateComingDisplayProduct.sumAssuredMinimum)
        )
      )
    }

    yield put(productSelection.changeProduct(updateComingDisplayProduct))

    if (previousDisplayProduct.code !== '' && previousDisplayProduct.code !== updateComingDisplayProduct.code) {
      yield put(partnerRequesting('change_product'))

      const comingAvailableRiders = yield call(getAvailableRiderByBasicPlanCode, updateComingDisplayProduct, false)
      yield put(productCommonCoveragePlan.updateAvailableRiders(comingAvailableRiders))

      yield fork(calculateWithValidation)
    }
  }

  function* defaultSelectModalFactor(updateComingDisplayProduct) {
    const selectedModalFactor = yield select(getSelectedModelFactorID)
    const selectedDisplayProduct = yield select(getSelectedDisplayProduct)
    const patchFunc =
      updateComingDisplayProduct.category === biMessages.CATEGORY_INVESTMENT
        ? productInvestmentCoveragePlan.patchModelFactor
        : productCommonCoveragePlan.patchModelFactor

    if (selectedDisplayProduct.basicPlanCode !== updateComingDisplayProduct.basicPlanCode) {
      yield put(patchFunc('model-factor_1'))
    }

    if (!updateComingDisplayProduct.modelFactors.includes(selectedModalFactor)) {
      if (updateComingDisplayProduct.modelFactors.length === 1) {
        yield put(patchFunc(updateComingDisplayProduct.modelFactors[0]))
      } else if (updateComingDisplayProduct.modelFactors.includes('model-factor_1')) {
        yield put(patchFunc('model-factor_1'))
      } else if (updateComingDisplayProduct.modelFactors.includes('model-factor_2')) {
        yield put(patchFunc('model-factor_2'))
      } else if (updateComingDisplayProduct.modelFactors.includes('model-factor_3')) {
        yield put(patchFunc('model-factor_3'))
      } else if (updateComingDisplayProduct.modelFactors.includes('model-factor_4')) {
        yield put(patchFunc('model-factor_4'))
      }
    }
  }

  function* onSelectModelFactor(action) {
    yield put(updateFormState({ dirty: true }))
    const modelFactorId = action.payload
    yield put(partnerRequesting('change_model_factor'))
    yield call(calculateWithValidation, { modelFactor: modelFactorId })
  }

  function* onSelectPensionType(action) {
    const pensionType = action.payload
    yield put(partnerRequesting('change_pension_type'))
    yield fork(calculateWithValidation, { pensionType })
  }

  function* onCalculateFromSumAssuredChange(action) {
    yield put(updateFormState({ dirty: true }))
    const comingSumAssured = action.payload

    const selectedDisplayProduct = yield select(getSelectedDisplayProduct)
    const comingProduct = yield call(findAvailableInProductGroup, {
      selectedDisplayProduct,
      sumAssured: comingSumAssured,
    })
    if (comingProduct) {
      yield put(productSelection.changeProduct(comingProduct))
    }

    yield put(partnerRequesting('change_sum_assured'))
    yield fork(calculateWithValidation, { sumAssured: comingSumAssured })
  }

  function* findAvailableInProductGroup({ selectedDisplayProduct, sumAssured, insuredAge }) {
    if (selectedDisplayProduct.productGroups && Array.isArray(selectedDisplayProduct.productGroups)) {
      const filterSumAssured = sumAssured ?? (yield select(getSumAssured))
      const filterInsuredAge = insuredAge ?? (yield select(getAge)).value
      const availableDisplayProducts = yield select(getPartnerAvailableDisplayProduct)

      const availableProducts = selectedDisplayProduct.productGroups
        .filter((code) => availableDisplayProducts[code])
        .map((code) => availableDisplayProducts[code])
        .reduce((arr, curr) => [...arr, ...curr], [])
      const comingProducts = availableProducts.filter(
        (product) =>
          filterInsuredAge >= product.entryAge.minimum.value &&
          filterInsuredAge <= product.entryAge.maximum.value &&
          filterSumAssured >= product.entrySumAssured.minimum &&
          filterSumAssured <= product.entrySumAssured.maximum
      )

      if (comingProducts.length === 1) {
        return comingProducts[0]
      }
    }
    return undefined
  }

  function* onCalculateFromPremiumChange(action) {
    yield put(updateFormState({ dirty: true }))
    yield put(partnerRequesting('change_premium'))
    yield fork(calculateWithValidation, { basicPremium: action.payload }, { calculateByPremium: true })
  }

  function* onToggleRider(action) {
    const { riderCode } = action.payload

    const displayProductCode = yield select(getSelectedDisplayProductCode)
    const availableRiders = yield select(getAvailableRiders)

    const currentRider = availableRiders.find((item) => item.code === riderCode)
    const selectedPlanCode = riderHandler.getRiderSelectedPlanCode(currentRider)
    const cacheAvailableRiders = yield select(getPartnerAvailableRiderByDisplayProductCode(displayProductCode))
    const rider = cacheAvailableRiders[selectedPlanCode]
    if (currentRider && rider) {
      let comingSelectedPlan = getRiderSelectedPlan({
        riderInGroups: undefined,
        riderState: currentRider,
        riderTarget: rider,
      })

      yield put(partnerRequesting('toggle_rider'))
      yield put(productCommonCoveragePlan.editRiderSelectedPlan(riderCode, comingSelectedPlan))
    }
  }

  function* onEditRiderSelectPlan(action) {
    const { code, selectedPlan } = action.payload
    const { planCode, fullPlanCode, sumAssured } = selectedPlan
    const selectPlan = fullPlanCode?.length ? fullPlanCode : planCode?.length ? planCode : code

    const selectedDisplayProductCode = yield select(getSelectedDisplayProductCode)

    let comingSumAssured
    if (!_.isUndefined(sumAssured)) {
      comingSumAssured = sumAssured
    } else {
      const riderStates = yield select(getAvailableRidersByCode)
      const riderState = riderStates[code]
      const cacheAvailableRiders = yield select(
        getPartnerAvailableRiderByDisplayProductCode(selectedDisplayProductCode)
      )
      const rider = cacheAvailableRiders[selectPlan]
      if (rider.fixedSA === 'Y') {
        comingSumAssured = rider.minSA
      } else {
        if (riderState.sumAssured !== rider.defaultSA) {
          comingSumAssured = riderState.sumAssured
        } else {
          comingSumAssured = rider.defaultSA
        }
      }
    }

    yield put(partnerRequesting('change_rider_plan'))
    yield put(productCommonCoveragePlan.editRiderSumAssured(code, comingSumAssured))
  }

  function* onCalculateFromRiderSumAssuredChange(_action) {
    yield put(updateFormState({ dirty: true }))
    yield put(partnerRequesting('change_rider_sum_assured'))
    yield fork(calculateWithValidation)
  }

  function* onCalculateFromRiderPremiumChange(action) {}

  return {
    getDisplayProductFromCache,
    getAvailableRidersByDisplayProductCode,
    getProductCategories,
    getProductPackageBySelectedProduct,
    getFnaConfiguration,
    calculateWithValidation,
    onSelectProduct,
    onUpdateSelectedBasicPlan,
    onChangeProduct,
    onCalculateFromSumAssuredChange,
    onCalculateFromPremiumChange,
    getBasicPlan,
    onSelectModelFactor,
    onSelectPensionType,
    onToggleRider,
    onEditRiderSelectPlan,
    onCalculateFromRiderSumAssuredChange,
    onCalculateFromRiderPremiumChange,
    getAvailableRiderByBasicPlanCode,
    findAvailableInProductGroup,
    defaultSelectModalFactor,
  }
}
